import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Capacitor } from '@capacitor/core';
import { ArrowRight, Building2, Lock, Mail, ShieldCheck } from 'lucide-react';
import { Button, Input, cn } from '@hrms/ui';
import { ME_QUERY_KEY, tokens, tsr } from '../lib/api.js';
import { useSession } from '../session/SessionProvider.js';
import { currentOrgSlug, isDevOffAppDomain, orgSubdomainOrigin } from '../lib/subdomain.js';
import {
  AuthMethodChooser,
  BackToMethods,
  useAuthMethod,
  useSsoProviders,
} from '../features/auth/AuthMethodChooser.js';
import { AuthAlert, AuthCard, AuthHeading, AuthLayout, PasswordToggle } from './auth/AuthLayout.js';
import { authInputClass, authLinkClass, authSubmitClass } from './auth/authStyles.js';
import { loginIdentifier, loginIdentifierProblem, WORKSPACE_DOMAIN } from './auth/authFields.js';
import { trackAppLogin } from '../lib/meta-app-events.js';

/**
 * Only ever return to a path on THIS origin. `state.from` is client-controlled (it survives a
 * copy-pasted URL), and an absolute or protocol-relative value here would be an open redirect.
 */
function safeReturnPath(state: unknown): string {
  const from = (state as { from?: unknown } | null)?.from;
  return typeof from === 'string' && from.startsWith('/') && !from.startsWith('//') ? from : '/';
}

type Stage = 'credentials' | 'mfa';
type FieldName = 'orgSlug' | 'email' | 'password' | 'totpCode';

/** What is missing or malformed before anything is sent, so the API's generic 422 is rarely seen. */
function checkFields(
  stage: Stage,
  values: { orgSlug: string; email: string; password: string; totpCode: string },
): Partial<Record<FieldName, string>> {
  if (stage === 'mfa') {
    return /^\d{6}$/.test(values.totpCode.trim()) ? {} : { totpCode: 'Enter the 6-digit code.' };
  }
  const problems: Partial<Record<FieldName, string>> = {};
  if (!values.orgSlug.trim()) problems.orgSlug = 'Enter your workspace URL.';
  const emailIssue = loginIdentifierProblem(values.email);
  if (emailIssue) problems.email = emailIssue;
  if (!values.password) problems.password = 'Enter your password.';
  return problems;
}

export function Login() {
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Locked to the tenant subdomain when there is one — see lib/subdomain.ts. A company reached at
  // its own `arham.lvh.me` URL never needs to be told which workspace it's signing into; the host
  // already said so, at the same trust level as typing it (see orgSlugSchema's doc comment).
  const [subdomainSlug] = useState(() => currentOrgSlug());
  const [orgSlug, setOrgSlug] = useState(subdomainSlug ?? '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [stage, setStage] = useState<Stage>('credentials');
  // Set just before the tokens are saved: from then on `onSuccess` owns the redirect.
  const [signingIn, setSigningIn] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldName, string>>>({});

  const login = tsr.auth.login.useMutation();
  const sessionHandoff = tsr.auth.sessionHandoff.useMutation();

  // Two ways in: a provider (Zoho) or email + password. With no provider to offer, the email form
  // shows straight away; mid two-factor it always stays on the form.
  const sso = useSsoProviders();
  const { method, chooseEmail, backToChoices } = useAuthMethod();
  const hasProviders = sso.loading || sso.providers.length > 0;
  const choosing = stage === 'credentials' && method === 'choose' && hasProviders;

  if (session.status === 'authenticated') {
    // Mid sign-in, `onSuccess` below owns the redirect (this origin, or a handoff to the org's
    // subdomain). Saving the tokens flips the session to authenticated once /me loads, and that
    // re-render can land before `onSuccess` reaches its own navigate — a `<Navigate to="/">` here
    // then dropped a deep link (a WhatsApp'd live-session join link) on Home.
    if (signingIn) return null;
    return <Navigate to={safeReturnPath(location.state)} replace />;
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const problems = checkFields(stage, { orgSlug, email, password, totpCode });
    setFieldErrors(problems);
    if (Object.keys(problems).length > 0) return;
    login.mutate(
      {
        body: {
          orgSlug,
          // An email, or a mobile number for someone added with only a phone (see loginIdentifier).
          email: loginIdentifier(email),
          password,
          ...(stage === 'mfa' && totpCode ? { totpCode } : {}),
        },
      },
      {
        onSuccess: async (response) => {
          if (response.status !== 200) {
            return;
          }
          const { body } = response;

          if (typeof body !== 'object' || !body || !('result' in body)) {
            // If we received 200 HTML from Capacitor's local web server interception,
            // we manually throw to display a network error message.
            throw new Error('Invalid response from server');
          }

          // The contract makes this a discriminated union so the MFA branch cannot be forgotten:
          // a nullable `tokens` would compile while silently logging nobody in.
          if (body.result === 'mfa_required') {
            setStage('mfa');
            return;
          }

          setSigningIn(true);
          await tokens.save(body.tokens);
          trackAppLogin();
          const returnPath = safeReturnPath(location.state);

          // The common case: already on this org's own subdomain (or there is no subdomain
          // scheme configured at all, or in local development on localhost/127.0.0.1 so that
          // Chrome treats the origin as a Secure Context for webcam & geolocation APIs) — or this
          // is the Capacitor-wrapped native app (see apps/web/capacitor.config.ts), which has no
          // browser address bar to rebrand and always runs from its own one fixed virtual origin.
          // The cross-origin handoff below assumes a real browser tab that can navigate away and
          // is happy to end up on a different origin; a WebView is neither — Capacitor blocks
          // navigating to a host outside `server.allowNavigation` by default, and even with that
          // configured, bouncing a packaged native app's WebView to a live network origin for the
          // rest of the session is a needless liability for a redirect that exists purely to put
          // the tenant's name in a URL bar the native app doesn't have.
          if (
            currentOrgSlug() === body.person.orgSlug ||
            window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1' ||
            isDevOffAppDomain() ||
            Capacitor.isNativePlatform()
          ) {
            await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
            navigate(returnPath, { replace: true });
            return;
          }

          // Logged in from the bare app domain, or from a DIFFERENT tenant's subdomain: the
          // session just saved lives in THIS origin's localStorage, which the destination origin
          // cannot read. A one-time handoff code is what lets that origin mint its own session
          // instead of landing signed out — see auth.contract.ts's sessionHandoff doc comment for
          // why this can't just be a plain redirect.
          try {
            const { body: handoff } = await sessionHandoff.mutateAsync({});
            window.location.href = `${orgSubdomainOrigin(body.person.orgSlug)}/session-handoff?code=${encodeURIComponent(handoff.code)}&next=${encodeURIComponent(returnPath)}`;
          } catch {
            // The handoff itself failed (network blip, rate limit) — fall back to staying on
            // this origin rather than stranding the user on a half-finished sign-in. They are
            // still correctly logged in here; only the branded URL is lost, not the session.
            await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
            navigate(returnPath, { replace: true });
          }
        },
      },
    );
  };

  const errorMessage = login.isError
    ? describeError(login.error, stage)
    : login.data && login.data.status !== 200
      ? describeError(login.data, stage)
      : null;

  return (
    <AuthLayout>
      <AuthCard>
        {stage === 'mfa' ? (
          <AuthHeading
            icon={<ShieldCheck className="size-5" />}
            title="Two-factor verification"
            description="Enter the 6-digit code from your authenticator app."
          />
        ) : choosing ? (
          <AuthHeading title="Welcome back" description="Choose how you'd like to sign in." />
        ) : (
          <AuthHeading
            title="Welcome back"
            description="Use the workspace URL your company chose when it signed up."
          />
        )}

        {choosing ? (
          // No workspace needed for a provider: a person with one login is signed straight in, one
          // with several is asked which. A company's own subdomain narrows it to that workspace.
          <AuthMethodChooser
            purpose="signin"
            providers={sso.providers}
            loading={sso.loading}
            orgSlug={subdomainSlug}
            next={safeReturnPath(location.state)}
            onChooseEmail={chooseEmail}
          />
        ) : (
        <form onSubmit={submit} className="flex flex-col gap-5" noValidate>
          {stage === 'credentials' && sso.providers.length > 0 && (
            <BackToMethods purpose="signin" onClick={backToChoices} className="-mt-2" />
          )}
          <Input
            label="Workspace"
            name="orgSlug"
            autoComplete="organization"
            autoCapitalize="none"
            placeholder="your-company"
            icon={<Building2 className="size-4" />}
            suffix={<span className="pr-1.5 text-xs text-fg-subtle">.{WORKSPACE_DOMAIN}</span>}
            className={cn(authInputClass, 'pr-32')}
            value={orgSlug}
            onChange={(e) => {
              setOrgSlug(e.target.value);
              setFieldErrors((f) => ({ ...f, orgSlug: undefined }));
            }}
            error={fieldErrors.orgSlug}
            readOnly={!!subdomainSlug || stage === 'mfa'}
            required
          />
          <Input
            label="Email or mobile number"
            name="email"
            // Not `email`: a mobile number must be typeable, and a browser would refuse it.
            type="text"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="username"
            placeholder="you@company.com or 98765 43210"
            icon={<Mail className="size-4" />}
            className={authInputClass}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setFieldErrors((f) => ({ ...f, email: undefined }));
            }}
            error={fieldErrors.email}
            readOnly={stage === 'mfa'}
            required
          />
          <div className="flex flex-col gap-2">
            <Input
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Enter your password"
              icon={<Lock className="size-4" />}
              suffix={
                stage === 'credentials' && (
                  <PasswordToggle shown={showPassword} onToggle={() => setShowPassword((v) => !v)} />
                )
              }
              className={cn(authInputClass, 'pr-11')}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setFieldErrors((f) => ({ ...f, password: undefined }));
              }}
              error={fieldErrors.password}
              readOnly={stage === 'mfa'}
              required
            />
            {stage === 'credentials' && (
              <Link to="/forgot-password" className={cn(authLinkClass, 'self-end text-sm')}>
                Forgot password?
              </Link>
            )}
          </div>
          {stage === 'mfa' && (
            <Input
              label="Authenticator code"
              name="totpCode"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              placeholder="000000"
              className={cn(authInputClass, 'text-center font-mono text-lg tracking-[0.5em]')}
              value={totpCode}
              onChange={(e) => {
                setTotpCode(e.target.value);
                setFieldErrors((f) => ({ ...f, totpCode: undefined }));
              }}
              error={fieldErrors.totpCode}
              autoFocus
              required
            />
          )}

          {errorMessage && <AuthAlert>{errorMessage}</AuthAlert>}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={login.isPending}
            className={authSubmitClass}
          >
            {stage === 'mfa' ? 'Verify and sign in' : 'Sign in'}
            {!login.isPending && (
              <ArrowRight
                className="size-4 transition-transform duration-200 group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            )}
          </Button>

          {stage === 'mfa' && (
            <Button
              type="button"
              variant="ghost"
              size="md"
              className="rounded-lg"
              onClick={() => {
                setStage('credentials');
                setTotpCode('');
                setFieldErrors({});
                login.reset();
              }}
            >
              Use a different account
            </Button>
          )}
        </form>
        )}
      </AuthCard>

      {stage === 'credentials' && (
        <p className="mt-6 text-center text-sm text-fg-muted">
          Don't have a workspace yet?{' '}
          <Link to="/signup" className={authLinkClass}>
            Create one
          </Link>
        </p>
      )}
    </AuthLayout>
  );
}

/**
 * Branch on status, not on message text. The one subtle case: a wrong TOTP code comes back as
 * the SAME 401 "email or password is incorrect" as a wrong password (the API does not distinguish
 * them, deliberately), so in the MFA stage that status means the code, not the credentials the
 * person already got right.
 */
function describeError(error: unknown, stage: Stage): string {
  if (error instanceof Error) return "Can't reach the server. Check your connection and try again.";

  const { status, body } = error as { status: number; body?: { message?: string } };
  switch (status) {
    case 429:
      return body?.message ?? 'Too many sign-in attempts. Wait a few minutes and try again.';
    case 401:
      return stage === 'mfa'
        ? "That code didn't work. Try again."
        : 'Email, mobile number or password is incorrect.';
    case 403:
      return body?.message ?? 'This account cannot sign in right now.';
    case 422:
      return 'Check your workspace address, email and password, then try again.';
    default:
      return body?.message ?? 'Something went wrong. Try again.';
  }
}
